/* eslint-disable react-hooks/exhaustive-deps */
// eslint-disable-next-line no-use-before-define
import React, { FC, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  OutlinedInput,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import { ReactComponent as CopyIcon } from '../../Asset/svg/copier.svg';
import { ReactComponent as FacebookSvg } from '../../Asset/svg/fb.svg';
import { ReactComponent as LinkedInSvg } from '../../Asset/svg/linkedin.svg';
import { ReactComponent as TwitterSvg } from '../../Asset/svg/x.svg';
import { ReactComponent as CheckWhiteSvg } from '../../Asset/svg/checkwhite.svg';
// @ts-ignore
import useStyles from './style';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  /** Label displayed after "Partager le" in the header (e.g. "test", "jeu", "ebook") */
  title: string;
  shareUrl: string;
  /** Prefix path displayed before the editable slug (e.g. ".../api/test/191/") */
  englishUrlPrefix: string;
  /** Current English slug value */
  englishSlug?: string;
  /** Called when the slug is saved */
  onSaveSlug: (slug: string) => void;
  /** Show social share buttons (Facebook, LinkedIn, X). Defaults to true */
  showSocialButtons?: boolean;
  /** Lang query param appended to the English share URL for Weglot. Defaults to "en" */
  englishLang?: string;
  /** Show a warning when modifying an existing English slug. Used for tests. */
  showSlugChangeWarning?: boolean;
  /** Base URL for the English share link (e.g. "https://discover.../api/test/191/"). When provided, replaces the domain derived from shareUrl. */
  englishBaseUrl?: string;
}

const URL_SLUG_PATTERN = /[^a-z0-9\-_.~]/g;

const sanitizeUrlSlug = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, '-').replace(URL_SLUG_PATTERN, '');

const ShareModal: FC<ShareModalProps> = ({
  open,
  onClose,
  title,
  shareUrl,
  englishUrlPrefix,
  englishSlug = '',
  onSaveSlug,
  showSocialButtons = true,
  englishLang = 'en',
  showSlugChangeWarning = false,
  englishBaseUrl,
}) => {
  const classes = useStyles();
  const [slug, setSlug] = useState(englishSlug);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [slugSaved, setSlugSaved] = useState(false);

  const buildEnglishUrl = (slugValue: string) => {
    const trimmedSlug = slugValue.trim();
    if (englishBaseUrl) {
      const base = englishBaseUrl.endsWith('/') ? englishBaseUrl : `${englishBaseUrl}/`;
      return `${base}${trimmedSlug}?lang=${englishLang}`;
    }
    const baseUrl = shareUrl.substring(0, shareUrl.lastIndexOf('/') + 1);
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${trimmedSlug}${separator}lang=${englishLang}`;
  };

  useEffect(() => {
    setSlug(englishSlug);
    // setSlugSaved(!!englishSlug);
  }, [englishSlug]);

  useEffect(() => {
    if (!snackbarOpen) return;
    const timer = setTimeout(() => setSnackbarOpen(false), 2500);
    return () => clearTimeout(timer);
  }, [snackbarOpen]);

  const handleCopyFr = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarOpen(true);
  };

  const handleCopyEn = () => {
    navigator.clipboard.writeText(buildEnglishUrl(slug));
    setSnackbarOpen(true);
  };

  const handleSave = () => {
    onSaveSlug(sanitizeUrlSlug(slug.trim()));
    setSlugSaved(true);
  };

  const handleSlugChange = (value: string) => {
    setSlug(sanitizeUrlSlug(value));
  };

  const isSlugFilled = slug.trim().length > 0;
  const hasChanges = slug.trim() !== (englishSlug || '').trim();
  const showSlugWarning =
    showSlugChangeWarning &&
    !!englishSlug.trim() &&
    hasChanges;

  const handleClose = () => {
    if (!slugSaved) {
      setSlug(englishSlug);
    }
    setSlugSaved(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={true}
      classes={{ paper: classes.dialogPaper }}>
      {snackbarOpen && (
        <div className={classes.toastWrapper}>
          <div className={classes.toastContent}>
            <CheckWhiteSvg className={classes.toastIcon} />
            <span className={classes.toastText}>Lien copié</span>
            <ClearIcon fontSize="small" onClick={() => setSnackbarOpen(false)} />
          </div>
        </div>
      )}

      <div className={classes.header}>
        <Typography className={classes.title}>
          Partager {title !== 'e-book' ? 'le ' : "l'"}{title}
        </Typography>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <ClearIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        {showSocialButtons && (
          <div className={classes.socialRow}>
            <FacebookShareButton url={shareUrl} className={classes.socialButtonWrapper}>
              <div className={classes.socialButton}>
                <FacebookSvg className={classes.socialIcon} />
                <span className={classes.socialLabel}>Facebook</span>
              </div>
            </FacebookShareButton>

            <LinkedinShareButton url={shareUrl} className={classes.socialButtonWrapper}>
              <div className={classes.socialButton}>
                <LinkedInSvg className={classes.socialIcon} />
                <span className={classes.socialLabel}>Linkedin</span>
              </div>
            </LinkedinShareButton>

            <TwitterShareButton url={shareUrl} className={classes.socialButtonWrapper}>
              <div className={classes.socialButton}>
                <TwitterSvg className={classes.socialIcon} />
                <span className={classes.socialLabel}>X</span>
              </div>
            </TwitterShareButton>
          </div>
        )}

        {/* French URL */}
        <div className={classes.section}>
          <Typography className={classes.label}>URL en version française</Typography>
          <div className={classes.urlRow}>
            <OutlinedInput
              value={shareUrl}
              readOnly={true}
              fullWidth={true}
              className={classes.urlInput}
            />
            <Button variant="outlined" className={classes.copyButton} onClick={handleCopyFr}>
              <CopyIcon />
            </Button>
          </div>
        </div>

        {/* English URL */}
        <div className={classes.section}>
          <Typography className={classes.label}>
            {slugSaved ? `Partager ce ${title} en version anglaise` : 'URL en version anglaise'}
          </Typography>
          <div className={classes.urlRow}>
            <div className={classes.prefixBox}>
              <Typography className={classes.prefixText}>{englishUrlPrefix}</Typography>
            </div>
            <OutlinedInput
              value={slug}
              onChange={e => handleSlugChange(e.target.value)}
              fullWidth={true}
              className={slugSaved ? classes.slugInput : classes.slugInputExpanded}
            />
            {(slugSaved || englishSlug) && (
              <Button variant="outlined" className={classes.copyButton} onClick={handleCopyEn}>
                <CopyIcon />
              </Button>
            )}
          </div>
          <div className={classes.infoRow}>
            <InfoOutlinedIcon className={classes.infoIcon} />
            <Typography className={classes.infoText}>
              Renseignez le slug de l&apos;URL en anglais, puis validez.
            </Typography>
          </div>
        </div>

        {showSlugWarning && title === 'test' && (
          <div className={classes.slugWarningBox}>
            <WarningIcon className={classes.slugWarningIcon} />
            <Typography className={classes.slugWarningText}>
              <span className={classes.slugWarningBold}>Attention :</span> en modifiant ce slug,
              l&apos;ancienne URL en anglais ne sera plus accessible. Les clients qui poss&egrave;dent
              l&apos;ancien lien tomberont sur une page 404.
            </Typography>
          </div>
        )}

        {hasChanges && !slugSaved ? (
          <Button
            fullWidth={true}
            variant="contained"
            className={isSlugFilled ? classes.saveButtonActive : classes.saveButtonDisabled}
            onClick={handleSave}
            disabled={!isSlugFilled}>
            {englishSlug ? 'Enregistrer les modifications' : 'Enregistrer'}
          </Button>
        ) : (
          <Button
            fullWidth={true}
            variant="outlined"
            className={classes.closeButtonGrey}
            onClick={handleClose}>
            Fermer
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
